import { X, Zap, Sparkles, Bot } from 'lucide-react';

type CreateMenuModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreateAutomation: () => void;
  onCreateWithAi: () => void;
  onCreateAgent: () => void;
};

/**
 * "Create" menu for the Automations page: one entry point (the blue plus)
 * fanning out to build an automation, build one with AI, or create an agent.
 */
export default function CreateMenuModal({
  isOpen,
  onClose,
  onCreateAutomation,
  onCreateWithAi,
  onCreateAgent,
}: CreateMenuModalProps) {
  if (!isOpen) return null;

  const pick = (action: () => void) => {
    onClose();
    action();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60">
          <div>
            <h2 className="text-lg font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
              Create new
            </h2>
            <p className="text-sm text-gray-600" style={{ letterSpacing: '-0.01em' }}>
              What would you like to build?
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-2.5">
          <button
            onClick={() => pick(onCreateAutomation)}
            className="w-full text-left p-4 rounded-[12px] border border-blue-200 bg-blue-50/60 hover:bg-blue-50 active:bg-blue-100 transition-colors flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-[10px] bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Automation
              </div>
              <div className="text-sm text-gray-600 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                Build a workflow from scratch — pick a trigger, add actions.
              </div>
            </div>
          </button>

          <button
            onClick={() => pick(onCreateWithAi)}
            className="w-full text-left p-4 rounded-[12px] border border-indigo-200 bg-indigo-50/60 hover:bg-indigo-50 active:bg-indigo-100 transition-colors flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-[10px] bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                AI create automation
              </div>
              <div className="text-sm text-gray-600 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                Describe what you want and AI builds the automation for you.
              </div>
            </div>
          </button>

          <button
            onClick={() => pick(onCreateAgent)}
            className="w-full text-left p-4 rounded-[12px] border border-gray-300 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-start gap-3.5"
          >
            <div className="w-10 h-10 rounded-[10px] bg-gray-900 flex items-center justify-center flex-shrink-0">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Create agent
              </div>
              <div className="text-sm text-gray-600 leading-snug mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                An AI teammate like the receptionist that answers your calls.
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
